import { Button } from "@crew/ui/components/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@crew/ui/components/field";
import { Input } from "@crew/ui/components/input";
import { Link } from "@crew/ui/components/link";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError, errorHint } from "../../app/api";
import { AuthCard } from "./auth-card";
import { startSession } from "./logout";

type Form = { name: string; workspace_name: string; email: string; password: string; code: string };

/** Signs up, then starts the new person's session in this tab and asks for the emailed code (#384). */
function useSignup(form: Form) {
  const navigate = useNavigate();
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api("POST", "/api/auth/signup", {
        ...form,
        code: form.code || undefined,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    onSuccess: async () => {
      await startSession(client);
      navigate("/verify", { replace: true });
    },
  });
}

function SignupFields({
  form,
  set,
  closed,
}: {
  form: Form;
  set: (key: keyof Form) => (e: { target: { value: string } }) => void;
  closed: boolean;
}) {
  return (
    <>
      <Field>
        <FieldLabel htmlFor="name">Your name</FieldLabel>
        <Input
          id="name"
          autoComplete="name"
          required
          maxLength={80}
          value={form.name}
          onChange={set("name")}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="workspace_name">Workspace name</FieldLabel>
        <Input
          id="workspace_name"
          required
          maxLength={80}
          value={form.workspace_name}
          onChange={set("workspace_name")}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="email">Email</FieldLabel>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          required
          value={form.email}
          onChange={set("email")}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="password">Password</FieldLabel>
        <Input
          id="password"
          type="password"
          autoComplete="new-password"
          required
          minLength={10}
          value={form.password}
          onChange={set("password")}
        />
        <FieldDescription>At least 10 characters.</FieldDescription>
      </Field>
      <Field>
        <FieldLabel htmlFor="code">Invite code</FieldLabel>
        <Input id="code" autoComplete="off" value={form.code} onChange={set("code")} />
        <FieldDescription>
          {closed ? "Signups need a valid invite code." : "If you were given one."}
        </FieldDescription>
      </Field>
    </>
  );
}

/** Signups are closed to anyone without a valid invite code. */
const isClosed = (error: unknown) => error instanceof ApiError && error.code === "signup_closed";

/** Why it failed (a closed signup says so under the code instead), and the button. */
function SignupSubmit({ error, pending }: { error: unknown; pending: boolean }) {
  return (
    <>
      {error && !isClosed(error) ? <FieldError>{errorHint(error)}</FieldError> : null}
      <Button type="submit" disabled={pending}>
        {pending ? "Creating…" : "Create workspace"}
      </Button>
    </>
  );
}

export function SignupPage() {
  const [form, setForm] = useState<Form>({
    name: "",
    workspace_name: "",
    email: "",
    password: "",
    code: "",
  });
  const set = (key: keyof Form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));
  const signup = useSignup(form);
  const closed = isClosed(signup.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    signup.mutate();
  };

  return (
    <AuthCard
      title="Create your workspace"
      description="Your Chief of Staff and their computer come with it."
      footer={
        <>
          Already have an account?{" "}
          <Link href="/login" className="underline">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={submit}>
        <FieldGroup>
          <SignupFields form={form} set={set} closed={closed} />
          <SignupSubmit error={signup.error} pending={signup.isPending} />
        </FieldGroup>
      </form>
    </AuthCard>
  );
}
